import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse
} from 'axios';
import { omit } from 'lodash';

import { useLocalStorage } from '~hooks/index';
import { type ErrorType, useAppStorage } from '~store/useStore';
import { cleanObject } from '~utils/index';

export interface RequestParams {
  length?: number;
  page?: number;
  [key: string]: unknown;
}

export interface CatchError {
  response: { data: ErrorType };
}

interface SendRequestProps<T> {
  url: string;
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
  data?: object;
  params?: RequestParams;
  isDatatable?: boolean;
  thenFunction: (response: AxiosResponse<T>) => void;
  catchFunction?: (error: AxiosError) => void;
  finallyFunction?: () => void;
  type?: 'private' | 'public' | 'icValidator';
  domainRequest?: boolean;
  extraConfig?: Record<string, string>;
}

const sendRequest = async <T>({
  url,
  method = 'get',
  data = {},
  params = {},
  isDatatable = false,
  extraConfig = {},
  thenFunction,
  catchFunction,
  finallyFunction,
  type = 'private'
}: SendRequestProps<T>) => {
  const { userData } = useLocalStorage();
  const { requestFinalized, saveUserError } = useAppStorage.getState();

  const token = {
    private: `Bearer ${userData?.tokens?.access}`,
    public: undefined,
    icValidator: `Bearer ${userData?.tokens?.access}`
  }[type];

  const baseURL =
    type === 'icValidator'
      ? import.meta.env.VITE_IC_VALIDATOR_URL
      : import.meta.env.VITE_BACKEND_URL;

  const prefix =
    import.meta.env.MODE === 'development' ? 'http://' : 'https://';
  const sufix = import.meta.env.MODE === 'development' ? ':8000' : '';
  let finalBaseURL = `${baseURL}`;
  if (userData?.tenantSelected?.domain && type !== 'icValidator') {
    finalBaseURL = `${prefix}${userData.tenantSelected.domain}${sufix}`;
  }

  let finalParams: RequestParams = params;
  if (isDatatable) {
    const length = params.length ?? 0;
    const page = params.page ?? 0;

    finalParams = omit(
      {
        format: 'datatables',
        length: params.length,
        start: length * page || 0,
        ...params
      },
      ['page']
    );
  }

  const recaptchaRaw = localStorage.getItem('recaptcha');
  const recaptchaParse: string = recaptchaRaw
    ? (JSON.parse(recaptchaRaw) as string)
    : '';

  const config: AxiosRequestConfig = cleanObject({
    baseURL: finalBaseURL,
    url,
    method,
    data,
    params: finalParams,
    headers: cleanObject({
      Authorization: token,
      recaptcha: recaptchaParse
    }),
    ...extraConfig
  });

  await axios(config)
    .then((response: AxiosResponse<T>) => thenFunction(response))
    .catch((error: AxiosError) => {
      const userError = error as CatchError;
      requestFinalized();
      saveUserError(userError.response?.data);
      catchFunction?.(error);
    })
    .finally(() => finallyFunction?.());
};

export default sendRequest;
